/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import type React from 'react';

import { type CommonProps } from '../_common/types';
import { FieldsetBase, type FieldsetProps } from '../fieldset/Fieldset';

type RadioGroupProps = FieldsetProps & {
  /** Shared native name for the radios in the group */
  name: string;
  /** The value of the selected radio. Pass `null` when no radio is selected. Leave undefined for uncontrolled usage */
  value?: string | null;
  /** Called with the newly selected radio's value when the selection changes */
  onChange?: (
    value: string,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  /** Whether the radios are stacked vertically or laid out in a wrapping row */
  orientation?: 'horizontal' | 'vertical';
};

/**
 * Groups atomic `Radio` components under a shared name and accessible legend.
 *
 * @remarks
 * Pass `value` and `onChange` to control the selection from the group; each
 * `Radio` is then checked when its `value` matches the group's `value`, and
 * its own `isChecked` is ignored. Without `value`, selection state remains on
 * each `Radio` (for example via `isDefaultChecked`), and `onChange` can still
 * be used to observe changes. Radios inherit `name`, disabled, required,
 * help, and validation semantics from the group.
 */
const RadioGroup = ({
  name,
  value,
  onChange,
  orientation = 'vertical',
  isDisabled = false,
  isRequired = false,
  htmlAttributes,
  ...props
}: CommonProps<'fieldset', RadioGroupProps>) => (
  <FieldsetBase
    {...props}
    orientation={orientation}
    isDisabled={isDisabled}
    isRequired={isRequired}
    fieldGroup={{
      controlType: 'radio',
      name,
      onChange:
        onChange === undefined
          ? undefined
          : (event) => onChange(event.target.value, event),
      value,
    }}
    htmlAttributes={{
      ...htmlAttributes,
      'aria-required': isRequired || undefined,
      role: 'radiogroup',
    }}
  />
);

RadioGroup.displayName = 'RadioGroup';

export { RadioGroup };
